/* eslint-disable @angular-eslint/directive-selector */
import { Directive, EventEmitter, Host, OnDestroy, Output, Self } from '@angular/core';
import { IVideoROIFrame } from './video-rois-viewer.directive';
import { Subject } from 'rxjs';

import { VideoViewerComponent } from '../components/video-viewer/video-viewer.component';

enum VideoROIOperation {
  LEFT_RESIZE = 'leftResize',
  RIGHT_RESIZE = 'rightResize',
  SHIFT = 'shift',
}

@Directive({
    selector: 'ts-video-viewer:[videoTrackPanel]',
    exportAs: 'videoROIs',
    standalone: false
})
export class VideoTrackPanelDirective implements OnDestroy {
  @Output() roiTimeUpdated = new EventEmitter<{
    roi: IVideoROIFrame;
    t1: number;
    t2: number;
  }>();

  private _destroy: Subject<void> = new Subject();
  protected tempT1;
  protected tempT2;
  protected initialDraggingPoint;
  protected decreasingSize = false;

  public operation: VideoROIOperation;

  constructor(@Host() @Self() protected viewerComponent: VideoViewerComponent) {}

  ngOnDestroy(): void {
    this._destroy.next();
  }

  onMouseDown(event: MouseEvent) {
    const roiTrackRect = this.viewerComponent.trackBar.nativeElement.querySelector('#roiTrack').getBoundingClientRect();

    this.initialDraggingPoint = event.clientX - roiTrackRect.left;
  }

  onMouseUp(event: MouseEvent) {
    // this.initialDraggingPoint = undefined;
  }

  onDragMove(roi: IVideoROIFrame, event) {
    const dragElement = this.viewerComponent.trackBar.nativeElement.querySelector(`[data-id="${roi.id}"]`);

    //Prevent from default shifting
    dragElement.style.transform = 'translate3d(0, 0, 0)';

    const initialOffSet = this.viewerComponent.trackBar.nativeElement.getBoundingClientRect().left;

    const currentCursorPos = event.event.clientX - initialOffSet;

    if (this.operation === VideoROIOperation.LEFT_RESIZE) {
      if (currentCursorPos > this.initialDraggingPoint) {
        // Cursor moved to the right, switch to right resize
        this.operation = VideoROIOperation.RIGHT_RESIZE;
        this.decreasingSize = true;
      }
    } else if (this.operation === VideoROIOperation.RIGHT_RESIZE) {
      if (currentCursorPos < this.initialDraggingPoint) {
        // Cursor moved to the left, switch to left resize
        this.operation = VideoROIOperation.LEFT_RESIZE;
        this.decreasingSize = true;
      }
    }
    this.handleResizeOrShift(roi, event);
  }

  startDraggingROI(roi: IVideoROIFrame, event) {
    const clientWidth = this.viewerComponent.trackBar.nativeElement.clientWidth;
    const resizeThreshold = 5;
    const relativeT1 = (roi.t1 * clientWidth) / this.viewerComponent.videoDuration;
    const relativeT2 = (roi.t2 * clientWidth) / this.viewerComponent.videoDuration;
    const t2ResizeLimit = relativeT2 - resizeThreshold;
    const t1ResizeLimit = relativeT1 + resizeThreshold;

    const isResizingLeft = this.initialDraggingPoint <= t1ResizeLimit;
    const isResizingRight = this.initialDraggingPoint >= t2ResizeLimit;

    const isShifting = this.initialDraggingPoint > t1ResizeLimit && this.initialDraggingPoint < t2ResizeLimit;

    if (isResizingLeft) this.operation = VideoROIOperation.LEFT_RESIZE;
    if (isResizingRight) this.operation = VideoROIOperation.RIGHT_RESIZE;
    if (isShifting) this.operation = VideoROIOperation.SHIFT;
  }

  private handleResizeOrShift(roi: IVideoROIFrame, event) {
    const timeDelta =
      (event.distance.x / this.viewerComponent.trackBar.nativeElement.clientWidth) * this.viewerComponent.videoDuration;

    if (this.operation === VideoROIOperation.LEFT_RESIZE) {
      const newT1 = roi.t1 + (this.decreasingSize ? 0 : timeDelta);
      const newT2 = roi.t2 + (this.decreasingSize ? timeDelta : 0);

      this.tempT1 = Math.min(newT1, newT2);
      this.tempT2 = Math.max(newT1, newT2);
    } else if (this.operation === VideoROIOperation.RIGHT_RESIZE) {
      const newT1 = roi.t1 + (this.decreasingSize ? timeDelta : 0);
      const newT2 = roi.t2 + (this.decreasingSize ? 0 : timeDelta);
      this.tempT1 = Math.min(newT1, newT2);
      this.tempT2 = Math.max(newT1, newT2);
    } else if (this.operation === VideoROIOperation.SHIFT) {
      this.tempT1 = roi.t1 + timeDelta;
      this.tempT2 = roi.t2 + timeDelta;
    }

    // Apply visual feedback: use the temporary values for t1 and t2
    this.updateROIVisual(roi, this.tempT1, this.tempT2);
  }

  updateROIVisual(roi: IVideoROIFrame, tempT1: number, tempT2: number) {
    const roiElement = this.viewerComponent.trackBar.nativeElement.querySelector(`[data-id="${roi.id}"]`);
    const clientWidth = this.viewerComponent.trackBar.nativeElement.clientWidth;

    const leftPosition = (tempT1 / this.viewerComponent.videoDuration) * clientWidth;
    const width = ((tempT2 - tempT1) / this.viewerComponent.videoDuration) * clientWidth;

    roiElement.style.left = `${leftPosition}px`;
    roiElement.style.width = `${width}px`;
  }

  processBookMarkDrag(roi: IVideoROIFrame, event) {
    const timeDelta =
      (event.distance.x / this.viewerComponent.trackBar.nativeElement.clientWidth) * this.viewerComponent.videoDuration;

    const maxtime = this.viewerComponent.videoDuration;

    if (this.operation === VideoROIOperation.LEFT_RESIZE) {
      let t1 = roi.t1;
      let t2 = roi.t2 + timeDelta;
      if (!this.decreasingSize) {
        t1 = roi.t1 + timeDelta;
        t2 = roi.t2;
      }
      this.roiTimeUpdated.emit({
        roi,
        t1: Math.max(0, Math.min(t1, t2)),
        t2: Math.min(maxtime, Math.max(t1, t2)),
      });
    } else if (this.operation === VideoROIOperation.RIGHT_RESIZE) {
      let t1 = roi.t1 + timeDelta;
      let t2 = roi.t2;
      if (!this.decreasingSize) {
        t1 = roi.t1;
        t2 = roi.t2 + timeDelta;
      }
      this.roiTimeUpdated.emit({
        roi,
        t1: Math.max(0, Math.min(t1, t2)),
        t2: Math.min(maxtime, Math.max(t1, t2)),
      });
    } else {
      const time = (roi.t1 || 0) + timeDelta;

      //Prevent from exceeding video duration

      const newtime = Math.min(maxtime - (roi.t2 - roi.t1), Math.max(0, time));

      this.roiTimeUpdated.emit({
        roi,
        t1: newtime < 0 ? 0 : newtime, //Prevent negative values
        t2: undefined,
      });
      this.viewerComponent.gotoBookmark(null, { ...roi, t1: time });
    }

    this.decreasingSize = false;
  }
}
